import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert } from '@mui/material';
import { getDefaultTenantId } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import { mintAppToken } from './api';
import { resolveExposeExecuteTenant } from './exposeTenantHonesty';

export default function MintTokenTab() {
  const selectedTenant = useAuthStore((s) => s.selectedTenant);
  const sessionTenant = resolveExposeExecuteTenant({
    selectedTenant,
    hostTenant: getDefaultTenantId(),
  });
  const [tenantId, setTenantId] = useState(sessionTenant || '');
  const [apiKey, setApiKey] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionTenant) setTenantId(sessionTenant);
  }, [sessionTenant]);

  async function onMint() {
    setLoading(true);
    setError(null);
    setToken(null);
    try {
      const res = await mintAppToken({ tenantId, apiKey });
      setToken(res.token);
    } catch (e: any) {
      setError(e?.message || 'Failed to mint token');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Mint an app JWT</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use a consumer API key to mint a short-lived, tenant-bound JWT for calling <code>/public/*</code> endpoints.
          Tenant defaults from the current session{sessionTenant ? ` (${sessionTenant})` : ''} — not a hard-coded <code>dev</code>.
        </Typography>

        <Box sx={{ display: 'grid', gap: 2, maxWidth: 720 }}>
          <TextField
            label="Tenant ID"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            size="small"
          />
          <TextField
            label="API Key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            size="small"
            type="password"
            placeholder="dk_live_..."
          />

          <Box>
            <Button variant="contained" onClick={onMint} disabled={loading || !tenantId || !apiKey}>
              {loading ? 'Minting...' : 'Mint Token'}
            </Button>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
          {token && (
            <Alert severity="success">
              Token minted. Copy from below.
              <Box sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  value={token}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Alert>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
